import React, { useState, useRef } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import './PhotoUpload.css';

const ASPECT_RATIOS = [
  { label: 'Square (1:1)', value: 1 },
  { label: 'Landscape (16:9)', value: 16 / 9 },
  { label: 'Portrait (9:16)', value: 9 / 16 },
  { label: 'Standard (4:3)', value: 4 / 3 },
  { label: 'Wide (3:2)', value: 3 / 2 },
];

const SIZES = ['8x10', '11x14', '16x20', '20x24', '24x36'];
const MATERIALS = ['Matte', 'Glossy', 'Canvas', 'Metal'];
const FINISHES = ['Standard', 'Premium', 'Framed'];

interface ProductOptions {
  size: string;
  material: string;
  finish: string;
}

const PhotoUpload: React.FC = () => {
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 5, y: 5, width: 90, height: 90 });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number>(1);
  const [productOptions, setProductOptions] = useState<ProductOptions>({
    size: SIZES[0],
    material: MATERIALS[0],
    finish: FINISHES[0],
  });
  const [uploading, setUploading] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const { success, error: showError, warning } = useToast();

  const onImageLoaded = (image: HTMLImageElement): boolean => {
    imgRef.current = image;
    return false;
  };

  const onCropComplete = (crop: PixelCrop): void => {
    setCompletedCrop(crop);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
        setCrop({ unit: '%', x: 5, y: 5, width: 90, height: 90 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAspectRatioChange = (ratio: number): void => {
    setAspectRatio(ratio);
    setCrop({ ...crop });
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleAddToCart = async (): Promise<void> => {
    if (!image || !completedCrop || !imgRef.current) {
      warning('Please upload and crop an image first.');
      return;
    }

    setUploading(true);

    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // Upload original image
      const formData = new FormData();
      formData.append('file', new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' }));
      const uploadResponse = await api.post<{ imageUrl: string }>('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const croppedImageUrl = uploadResponse.data.imageUrl;

      // Create product
      const productData = {
        name: `Custom Print - ${productOptions.size} ${productOptions.material}`,
        description: `Custom photo print: ${productOptions.size}, ${productOptions.material}, ${productOptions.finish}`,
        price: calculatePrice(),
        imageUrl: image,
        croppedImageUrl: croppedImageUrl,
        aspectRatio: `${aspectRatio}`,
        size: productOptions.size,
        material: productOptions.material,
        finish: productOptions.finish,
        isCustomPrint: true,
      };

      const productResponse = await api.post<{ id: number }>('/products', productData);
      const productId = productResponse.data.id;

      // Add to cart
      await api.post('/cart', { productId, quantity: 1 });

      success('Photo added to cart successfully!');
      setTimeout(() => {
        setImage(null);
        setCompletedCrop(null);
      }, 3000);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to add to cart. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const calculatePrice = (): number => {
    let basePrice = 19.99;
    if (productOptions.size === '16x20') basePrice = 29.99;
    if (productOptions.size === '20x24') basePrice = 39.99;
    if (productOptions.size === '24x36') basePrice = 49.99;
    if (productOptions.material === 'Canvas') basePrice += 10;
    if (productOptions.material === 'Metal') basePrice += 15;
    if (productOptions.finish === 'Premium') basePrice += 5;
    if (productOptions.finish === 'Framed') basePrice += 20;
    return basePrice;
  };

  return (
    <div className="photo-upload-page">
      <div className="container">
        <h1 className="page-title">Upload & Crop Your Photo</h1>

        <div className="upload-section">
          <div className="upload-area">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
              id="file-input"
            />
            <label htmlFor="file-input" className="file-label">
              {image ? 'Change Image' : 'Choose Photo to Upload'}
            </label>

            {image && (
              <div className="crop-container">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={onCropComplete}
                  aspect={aspectRatio}
                >
                  <img 
                    src={image} 
                    alt="Crop" 
                    onLoad={(e) => onImageLoaded(e.currentTarget)}
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      width: 'auto',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </ReactCrop>
              </div>
            )}
          </div>

          <div className="options-panel">
            <div className="option-group">
              <h3>Aspect Ratio</h3>
              <div className="aspect-ratio-buttons">
                {ASPECT_RATIOS.map((ratio) => (
                  <button
                    key={ratio.value}
                    type="button"
                    className={`btn ${aspectRatio === ratio.value ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => handleAspectRatioChange(ratio.value)}
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="option-group">
              <h3>Print Options</h3>
              <div className="form-group">
                <label>Size</label>
                <select
                  value={productOptions.size}
                  onChange={(e) => setProductOptions({ ...productOptions, size: e.target.value })}
                >
                  {SIZES.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Material</label>
                <select
                  value={productOptions.material}
                  onChange={(e) => setProductOptions({ ...productOptions, material: e.target.value })}
                >
                  {MATERIALS.map((material) => (
                    <option key={material} value={material}>{material}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Finish</label>
                <select
                  value={productOptions.finish}
                  onChange={(e) => setProductOptions({ ...productOptions, finish: e.target.value })}
                >
                  {FINISHES.map((finish) => (
                    <option key={finish} value={finish}>{finish}</option>
                  ))}
                </select>
              </div>

              <div className="price-display">
                <strong>Price: ${calculatePrice().toFixed(2)}</strong>
              </div>
            </div>

            <button
              onClick={handleAddToCart}
              className="btn btn-primary btn-block"
              disabled={!image || !completedCrop || uploading}
            >
              {uploading && <span className="btn-spinner" aria-hidden="true"></span>}
              {uploading ? 'Adding to Cart...' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUpload;
